import React, { useEffect, useRef, useState, useMemo } from "react";
import { ArrowBack, AssignmentOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { searchCustomers } from "../../api/customerApi";
import NoteSelect from "../../components/NoteSelect";
import backgroundPattern from "../../assets/pasbookpng.png";
import "./SalesOrder.css";
import "./ProformaInvoice.css";

const STORAGE_KEY = "customerPOs";
const today = () => new Date().toISOString().slice(0, 10);
const safeArray = (v) => (Array.isArray(v) ? v : []);
const money = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyItem = () => ({
  id: `${Date.now()}-${Math.random()}`,
  item: "",
  qty: 1,
  rate: 0,
  gst: 18,
});

function CustomerSearch({ value, onType, onSelect }) {
  const wrapRef = useRef(null);
  const dropRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState(null);
  const [localCustomers, setLocalCustomers] = useState([]);

  const customerName = (c) => c?.name || c?.displayName || c?.customer_name || c?.customerName || "";
  const customerContact = (c) => c?.contactNumber || c?.phone || c?.mobile || "";
  const customerEmail = (c) => c?.email || "";
  const customerGstin = (c) => c?.gstin || "";
  const customerAddress = (c) => c?.address || c?.billingAddress || c?.addressLine1 || "";

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    try { setLocalCustomers(safeArray(JSON.parse(localStorage.getItem("customers") || "[]"))); } catch { setLocalCustomers([]); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchCustomers({ name: query.trim() });
        const apiRows = Array.isArray(res?.data) ? res.data : safeArray(res?.data?.data || res?.data?.customers);
        const q = query.trim().toLowerCase();
        const merged = [...apiRows, ...localCustomers].filter((r) => customerName(r).toLowerCase().includes(q) || customerContact(r).includes(q));
        const seen = new Set();
        setCustomers(merged.filter((r) => {
          const key = String(r?.id ?? customerName(r)).toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return Boolean(customerName(r));
        }));
      } catch {
        const q = query.trim().toLowerCase();
        setCustomers(localCustomers.filter((r) => customerName(r).toLowerCase().includes(q)));
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [open, query, localCustomers]);

  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const update = () => {
      const rect = wrapRef.current.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 300), window.innerWidth - 24);
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const showAbove = below < 240 && above > below;
      const maxHeight = Math.max(160, Math.min(300, showAbove ? above : below));
      setStyle({ position: "fixed", left, top: showAbove ? rect.top - maxHeight - 6 : rect.bottom + 6, width, maxHeight, zIndex: 10050 });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [open, customers.length]);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) && dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const list = open && style ? (
    <div className="note-customer-search-win" style={style} ref={dropRef}>
      {loading ? (
        <div className="note-dropdown-state">Searching...</div>
      ) : customers.length === 0 ? (
        <div className="note-dropdown-state">No customers found.</div>
      ) : (
        customers.map((c, i) => (
          <button key={c?.id ?? `${customerName(c)}-${i}`} type="button" className="note-customer-option" onClick={() => { onSelect({ name: customerName(c), mobile: customerContact(c), email: customerEmail(c), gstin: customerGstin(c), address: customerAddress(c) }, c?.id ?? null); setQuery(customerName(c)); setOpen(false); }}>
            <strong>{customerName(c)}</strong>
            <span>{customerContact(c) || customerEmail(c) || "Customer"}</span>
          </button>
        ))
      )}
    </div>
  ) : null;

  return (
    <>
      <div className="note-customer-picker" ref={wrapRef}>
        <input className="note-field note-customer-input" value={query} placeholder="Type to search customer..." onChange={(e) => { setQuery(e.target.value); onType(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
      </div>
      {list}
    </>
  );
}

export default function CustomerPO() {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;
  const quotationData = location.state?.quotationData || null;

  const getSourceRef = () => {
    if (quotationData) return { type: 'quotation', id: quotationData.id, number: quotationData.quotationNo || `EST-${String(quotationData.id || '').slice(-6)}` };
    if (editData?.sourceRef) return editData.sourceRef;
    return null;
  };

  const mapQuotationItems = () => {
    if (!quotationData?.cylinders?.length) return [emptyItem()];
    return quotationData.cylinders.map((row) => ({
      id: `${Date.now()}-${Math.random()}`,
      item: row.cylinderType || row.cylinderName || '',
      qty: Number(row.qty || 1),
      rate: Number(row.rate || 0),
      gst: Number(row.gst || 18),
    }));
  };

  const [poNo, setPoNo] = useState(editData?.poNo || `CPO-${String(Date.now()).slice(-6)}`);
  const [poDate, setPoDate] = useState(editData?.poDate || today());
  const [customer, setCustomer] = useState(editData?.customer || quotationData?.customer?.name || "");
  const [customerId, setCustomerId] = useState(editData?.customerId || quotationData?.customerId || null);
  const [customerMobile, setCustomerMobile] = useState(editData?.customerMobile || quotationData?.customer?.mobile || "");
  const [customerEmail, setCustomerEmail] = useState(editData?.customerEmail || quotationData?.customer?.email || "");
  const [customerAddress, setCustomerAddress] = useState(editData?.customerAddress || quotationData?.customer?.address || "");
  const [customerGstin, setCustomerGstin] = useState(editData?.customerGstin || quotationData?.customer?.gstin || "");
  const [notes, setNotes] = useState(editData?.notes || quotationData?.notes || "");
  const [terms, setTerms] = useState(editData?.terms || quotationData?.terms || "");
  const sourceRef = getSourceRef();
  const [items, setItems] = useState(safeArray(editData?.items).length ? editData.items : mapQuotationItems());
  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => {
    let subtotal = 0;
    let gstTotal = 0;
    items.forEach((row) => {
      const amount = Number(row.qty || 0) * Number(row.rate || 0);
      const gst = (amount * Number(row.gst || 0)) / 100;
      subtotal += amount;
      gstTotal += gst;
    });
    return { subtotal, gstTotal, total: subtotal + gstTotal };
  }, [items]);

  const updateItem = (id, field, value) => {
    setItems((rows) => rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const addItem = () => setItems((rows) => [...rows, emptyItem()]);

  const removeItem = (id) => {
    if (items.length === 1) {
      toast.error("At least one item is required.");
      return;
    }
    setItems((rows) => rows.filter((row) => row.id !== id));
  };

  const save = (status) => {
    if (!customer.trim()) {
      toast.error("Customer name is required.");
      return;
    }
    if (!items.some((row) => row.item.trim())) {
      toast.error("Add at least one item.");
      return;
    }

    setSaving(true);
    try {
      const existing = safeArray(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"));
      const payload = {
        id: editData?.id || Date.now(),
        type: "Customer PO",
        poNo,
        poDate,
        customer,
        customerId,
        customerMobile,
        customerEmail,
        customerAddress,
        customerGstin,
        notes,
        terms,
        items,
        subtotal: totals.subtotal,
        gstTotal: totals.gstTotal,
        total: totals.total,
        status: status === "draft" ? "Draft" : "Confirmed",
        sourceRef,
        createdAt: editData?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const next = editData
        ? existing.map((r) => (r.id === editData.id ? payload : r))
        : [...existing, payload];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));

      toast.success(status === "draft" ? "Customer PO saved as draft." : "Customer PO recorded successfully.");
      setTimeout(() => navigate("/dashboard/customer-pos"), 600);
    } catch (error) {
      console.error("Customer PO save failed:", error);
      toast.error("Unable to save Customer PO.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="note-page">
      <div className="note-background-watermark" style={{ backgroundImage: `url(${backgroundPattern})` }} />
      <header className="note-header">
        <div className="note-header-left">
          <div className="note-page-icon"><AssignmentOutlined /></div>
          <div className="note-page-title-wrap">
            <h1>{editData ? "Edit" : "Record"} Customer PO</h1>
            <p>{quotationData ? `Created from ${quotationData.quotationNo || 'Quotation'}` : 'Record customer purchase order'}</p>
          </div>
        </div>
        <button type="button" className="note-header-back" onClick={() => navigate("/dashboard/customer-pos")}>
          <ArrowBack />
        </button>
      </header>

      <main className="note-shell">
        <section className="note-card">
          <div className="note-section-head">
            <h2>PO Details</h2>
          </div>
          <div className="note-form-grid">
            <label className="note-field-group">
              <span>PO Number</span>
              <input className="note-field" value={poNo} onChange={(e) => setPoNo(e.target.value)} />
            </label>
            <label className="note-field-group">
              <span>PO Date</span>
              <input type="date" className="note-field" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
            </label>
          </div>
        </section>

        <section className="note-card">
          <div className="note-section-head">
            <h2>Customer Details</h2>
          </div>
          <div className="note-form-grid">
            <label className="note-field-group">
              <span>Customer Name *</span>
              <CustomerSearch value={customer} onType={(t) => { setCustomer(t); setCustomerId(null); }} onSelect={(r, id) => { setCustomer(r.name); setCustomerMobile(r.mobile); setCustomerEmail(r.email); setCustomerGstin(r.gstin); setCustomerAddress(r.address); setCustomerId(id); }} />
            </label>
            <label className="note-field-group">
              <span>Mobile</span>
              <input className="note-field" value={customerMobile} onChange={(e) => setCustomerMobile(e.target.value)} placeholder="Mobile number" />
            </label>
            <label className="note-field-group">
              <span>Email</span>
              <input className="note-field" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="Email" />
            </label>
            <label className="note-field-group">
              <span>GSTIN</span>
              <input className="note-field" value={customerGstin} onChange={(e) => setCustomerGstin(e.target.value)} placeholder="GSTIN" />
            </label>
            <label className="note-field-group note-field-wide">
              <span>Address</span>
              <textarea className="note-field note-textarea" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} placeholder="Address" />
            </label>
          </div>
        </section>

        <section className="note-card">
          <div className="note-section-head">
            <h2>Items</h2>
            <button type="button" className="note-outline-btn" onClick={addItem}>+ Add Item</button>
          </div>
          <div className="note-table-wrap">
            <table className="note-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item / Description</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>GST %</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => {
                  const amount = Number(row.qty || 0) * Number(row.rate || 0);
                  const gstAmount = (amount * Number(row.gst || 0)) / 100;
                  return (
                    <tr key={row.id}>
                      <td>{index + 1}</td>
                      <td>
                        <input className="note-table-input" value={row.item} onChange={(e) => updateItem(row.id, "item", e.target.value)} placeholder="Item name" />
                      </td>
                      <td>
                        <input className="note-table-input note-number" type="number" min="1" value={row.qty} onChange={(e) => updateItem(row.id, "qty", e.target.value)} />
                      </td>
                      <td>
                        <input className="note-table-input note-number" type="number" min="0" step="0.01" value={row.rate} onChange={(e) => updateItem(row.id, "rate", e.target.value)} />
                      </td>
                      <td>
                        <NoteSelect className="note-table-input" value={row.gst} onChange={(v) => updateItem(row.id, "gst", v)} options={[{ value: "0", label: "0%" }, { value: "5", label: "5%" }, { value: "12", label: "12%" }, { value: "18", label: "18%" }, { value: "28", label: "28%" }]} />
                      </td>
                      <td className="note-amount">₹ {money(amount + gstAmount)}</td>
                      <td>
                        <button type="button" className="note-delete-btn" onClick={() => removeItem(row.id)} disabled={items.length === 1}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="note-card">
          <div className="note-summary">
            <div className="note-summary-row">
              <span>Subtotal</span>
              <strong>₹ {money(totals.subtotal)}</strong>
            </div>
            <div className="note-summary-row">
              <span>GST</span>
              <strong>₹ {money(totals.gstTotal)}</strong>
            </div>
            <div className="note-summary-grand">
              <span>Total</span>
              <strong>₹ {money(totals.total)}</strong>
            </div>
          </div>
        </section>

        <section className="note-card">
          <div className="note-form-grid">
            <label className="note-field-group note-field-wide">
              <span>Notes</span>
              <textarea className="note-field note-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" />
            </label>
            <label className="note-field-group note-field-wide">
              <span>Terms & Conditions</span>
              <textarea className="note-field note-textarea" value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Terms and conditions" />
            </label>
          </div>
        </section>

        <div className="note-actions">
          <button type="button" className="note-btn note-secondary" onClick={() => navigate("/dashboard/customer-pos")}>Cancel</button>
          <button type="button" className="note-btn note-secondary" onClick={() => save("draft")} disabled={saving}>{saving ? "Saving..." : "Save as Draft"}</button>
          <button type="button" className="note-btn note-primary" onClick={() => save("confirmed")} disabled={saving}>{saving ? "Saving..." : "Record PO"}</button>
        </div>
      </main>
    </div>
  );
}
